/**
 * Serviço para gerenciar a troca de unidade administrativa (setor)
 *
 * Este serviço centraliza a lógica de troca de unidade, evitando duplicação de código
 * e garantindo que as operações sejam realizadas em sequência correta. Também assegura
 * que a persistência no localStorage seja feita corretamente e que os parâmetros
 * administrativeUnitId sejam enviados em todas as requisições.
 */
import userService from './UserService';
import store from '@/store';
import EventBus from '@/utils/event-bus';

class AdminUnitSwitchService {
  /**
   * Retorna a unidade administrativa primária do usuário
   * @returns {Number|null} ID da unidade administrativa primária ou null
   */
  getPrimaryUnitId() {
    const user = store.getters.user;
    return user && user.administrative_unit ? user.administrative_unit.id : null;
  }

  /**
   * Verifica se existe uma unidade selecionada ou escolhe a primária
   * Garante que sempre haja uma unidade administrativa selecionada
   */
  ensureSelectedUnit() {
    const currentUnitId = store.getters.selectedAdministrativeUnit;
    const primaryUnitId = this.getPrimaryUnitId();
    const userUnits = store.getters.userAdministrativeUnits;

    // Se não há unidade selecionada ou a unidade selecionada não existe nas unidades do usuário
    if (!currentUnitId || !userUnits.some(u => u.id === currentUnitId)) {
      // Use a unidade primária se disponível
      if (primaryUnitId) {
        // Limpa o localStorage para evitar persistência incorreta
        localStorage.removeItem('selectedAdministrativeUnit');
        // Usa a unidade primária
        store.commit('setSelectedAdministrativeUnit', primaryUnitId);
        return primaryUnitId;
      }
      // Se não há unidade primária, mas há outras unidades, use a primeira
      else if (userUnits.length > 0) {
        const firstUnitId = userUnits[0].id;
        localStorage.removeItem('selectedAdministrativeUnit');
        store.commit('setSelectedAdministrativeUnit', firstUnitId);
        return firstUnitId;
      }
    }

    return currentUnitId;
  }

  /**
   * Inicia o processo de troca de unidade administrativa
   * @param {Number|String} unitId - ID da nova unidade administrativa
   * @returns {Promise} - Promise que resolve quando a troca for concluída
   */
  async switchAdminUnit(unitId) {
    // Garante que temos uma unidade válida e que é do tipo número
    unitId = unitId ? parseInt(unitId, 10) : this.getPrimaryUnitId();

    // Se não foi fornecida uma unidade e não há unidade primária
    if (!unitId) {
      console.error('Não foi possível determinar uma unidade administrativa válida');
      return Promise.reject(new Error('Unidade administrativa não encontrada'));
    }

    // Verificações iniciais para evitar operações desnecessárias
    const currentUnitId = store.getters.selectedAdministrativeUnit;
    const userUnits = store.getters.userAdministrativeUnits;

    // 1. Verifica se é a mesma unidade - evita requisições duplicadas
    if (currentUnitId === unitId) {
      console.log('Mesma unidade administrativa já selecionada, ignorando...');
      return Promise.resolve({ success: false, reason: 'same_unit' });
    }

    // 2. Verifica se a unidade existe nas unidades do usuário
    const unit = userUnits.find(u => u.id === unitId);
    if (!unit) {
      console.error(`Unidade administrativa ID ${unitId} não encontrada para o usuário`);
      return Promise.reject(new Error('Unidade administrativa não encontrada'));
    }

    try {
      // 3. Exibir loading e mensagem de carregamento
      store.commit('toggleLoader', { show: true });
      store.commit('toggleSnackbar', {
        show: true,
        timeout: 3000,
        color: 'info',
        message: 'Carregando dados da unidade selecionada...',
        messageIcon: 'mdi-refresh',
      });

      // 4. Atualiza a contagem de pastas como primeira operação
      // Isso garante um snapshot da contagem antes da alteração
      await store.dispatch('getFoldersCount');

      // 5. Salvar a unidade selecionada no localStorage para persistência
      localStorage.setItem('selectedAdministrativeUnit', unitId);

      // 6. Atualiza o estado com a nova unidade selecionada
      store.commit('setSelectedAdministrativeUnit', unitId);

      // 7. Atualiza os dados do usuário com a nova unidade selecionada
      const response = await userService.getUser();
      store.commit('setUser', response.data);
      localStorage.setItem('user', JSON.stringify(response.data));

      // 8. Notifica outros componentes sobre a mudança através de eventos
      EventBus.$emit('admin-unit-changed', { unitId, unit });
      window.dispatchEvent(new CustomEvent('admin-unit-changed', {
        detail: { unitId, unitName: unit.name }
      }));

      // 9. Define parâmetros padrão para atualização das pastas
      // Garantimos que o administrativeUnitId esteja sempre no payload
      const currentRoute = window.location.pathname;
      const defaultParams = {
        page: 1,
        limit: 20,
        order: 'desc',
        sort: 'effective_updated_at',
        searchText: '',
        folderTypeId: '',
        administrativeUnitId: unitId,
      };

      // 10. Atualiza os dados das pastas baseados na rota atual
      if (currentRoute.includes('/pastas/entrada')) {
        await store.dispatch('getFoldersInbox', defaultParams);
      } else if (currentRoute.includes('/pastas/nao-recebidos')) {
        await store.dispatch('getFoldersUnreceived', defaultParams);
      } else if (currentRoute.includes('/pastas/enviados')) {
        await store.dispatch('getFoldersSent', defaultParams);
      } else if (currentRoute.includes('/pastas/arquivados')) {
        await store.dispatch('getFoldersArchived', defaultParams);
      } else if (currentRoute.includes('/pastas/') && currentRoute.includes('/show')) {
        // Se estiver visualizando uma pasta específica, recarrega a página para atualizar os dados
        window.location.reload();
        return { success: true };
      }

      // 11. Atualiza a contagem de pastas após todas as outras operações
      // Garantimos uma segunda chamada para atualizar a contagem com a nova unidade
      // O pequeno timeout garante que a atualização não será ignorada pelo mecanismo anti-spam
      setTimeout(async () => {
        await store.dispatch('getFoldersCount');
      }, 300);

      // 12. Notifica o usuário sobre o sucesso da operação
      store.commit('toggleSnackbar', {
        show: true,
        timeout: 6000,
        color: 'success',
        message: `Unidade alterada para ${unit.name}`,
        messageIcon: 'mdi-check',
      });

      return { success: true };
    } catch (error) {
      console.error('Erro ao trocar unidade administrativa:', error);

      // Notifica o usuário sobre o erro e reverte para a unidade primária
      const primaryUnitId = this.getPrimaryUnitId();
      if (primaryUnitId) {
        store.commit('setSelectedAdministrativeUnit', primaryUnitId);
        localStorage.setItem('selectedAdministrativeUnit', primaryUnitId);
      }

      store.commit('toggleSnackbar', {
        show: true,
        timeout: 6000,
        color: 'error',
        message: 'Erro ao atualizar dados. Tente novamente.',
        messageIcon: 'mdi-alert-outline',
      });

      return Promise.reject(error);
    } finally {
      // Sempre desativa o loader ao final da operação
      store.commit('toggleLoader', { show: false });
    }
  }
}

export default new AdminUnitSwitchService();



// WEBPACK FOOTER //
// ./src/services/AdminUnitSwitchService.js